import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Input, Dropdown } from "@/ui";
import { ChevronDown, Search } from "lucide-react";
import { api, buildStorefrontApiPath } from "@/lib/api";
import { useCategoryTree } from "@/features/categories/api";

type SuggestItem = {
  type: "product" | "category";
  id: string;
  label: string;
  to: string;
};

type SuggestResponse = {
  items: SuggestItem[];
};

type SearchBoxProps = {
  term: string;
  setTerm: (value: string) => void;
  cat: string;
  setCat: (value: string) => void;
  onSearch: (params?: { q?: string; cat?: string }) => void;
  className?: string;
};

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 220;

function unwrapData<T>(input: unknown): T {
  const payload = input as { data?: T } | T;
  return ((payload as { data?: T })?.data ?? payload) as T;
}

export default function SearchBox({
  term,
  setTerm,
  cat,
  setCat,
  onSearch,
  className,
}: SearchBoxProps) {
  const nav = useNavigate();
  const [sp] = useSearchParams();

  useEffect(() => {
    const qParam = sp.get("q") || "";
    const catParam = sp.get("cat") || "all";
    if (qParam !== term) setTerm(qParam);
    if (catParam !== cat) setCat(catParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp]);

  const {
    data: categories,
    isLoading: catsLoading,
    isError: catsError,
  } = useCategoryTree();

  const safeCategories = useMemo(
    () => (Array.isArray(categories) ? categories : []),
    [categories],
  );

  const parentCats = useMemo(
    () => safeCategories.filter((c) => c.slug !== "all"),
    [safeCategories],
  );

  const currentCatLabel = useMemo(() => {
    if (cat === "all") return "Todas las categorías";
    for (const p of parentCats) {
      if (p.slug === cat) return `${p.name} (todo)`;
      const sub = p.sub?.find((s) => s.slug === cat);
      if (sub) return sub.name;
    }
    return "Todas las categorías";
  }, [cat, parentCats]);

  const [suggestions, setSuggestions] = useState<SuggestResponse | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  const searchBoxRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (!searchBoxRef.current) return;
      if (!searchBoxRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const raw = term.trim();

    if (!raw || raw.length < MIN_QUERY_LENGTH) {
      setSuggestions(null);
      setShowSuggestions(false);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    const timeoutId = setTimeout(async () => {
      try {
        setSuggestionsLoading(true);

        const res = await api.get(buildStorefrontApiPath("/search/suggest"), {
          params: { q: raw, limit: 6 },
          signal: controller.signal as any,
        });

        const data = unwrapData<SuggestResponse>(res.data);

        if (!cancelled) {
          setSuggestions({
            items: Array.isArray(data?.items) ? data.items : [],
          });

          if (
            raw.length >= MIN_QUERY_LENGTH &&
            document.activeElement === inputRef.current
          ) {
            setShowSuggestions(true);
          }
        }
      } catch (err: any) {
        if (
          cancelled ||
          err?.name === "AbortError" ||
          err?.code === "ERR_CANCELED" ||
          err?.message?.includes("canceled")
        ) {
          return;
        }
        console.error("Error fetching suggestions", err);
      } finally {
        if (!cancelled) setSuggestionsLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [term]);

  const categorySuggestions = useMemo(
    () => (suggestions?.items ?? []).filter((item) => item.type === "category"),
    [suggestions],
  );

  const productSuggestions = useMemo(
    () => (suggestions?.items ?? []).filter((item) => item.type === "product"),
    [suggestions],
  );

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSearch({});
    setShowSuggestions(false);
  }

  function handleSearchClick() {
    onSearch({});
    setShowSuggestions(false);
  }

  function handleSearchTerm(termOverride?: string, catOverride?: string) {
    onSearch({ q: termOverride, cat: catOverride });
    setShowSuggestions(false);
  }

  function handleSuggestionClick(item: SuggestItem) {
    setShowSuggestions(false);

    if (item.type === "category") {
      const url = new URL(item.to, window.location.origin);
      const nextCat = url.searchParams.get("category") || "all";
      setCat(nextCat);
      onSearch({ q: "", cat: nextCat });
      return;
    }

    nav(item.to);
  }

  return (
    <form
      onSubmit={handleSubmit}
      role="search"
      aria-label="Buscar productos"
      className={`w-full justify-self-stretch ${className ?? ""}`}
    >
      <div className="relative" ref={searchBoxRef}>
        <div
          className="
            group flex items-stretch w-full
            rounded-xl
            bg-[rgb(var(--bg-rgb))]
            border border-[rgb(var(--border-rgb))]
            focus-within:ring-2 focus-within:ring-[rgb(var(--ring-rgb))]
            focus-within:border-transparent
            transition
          "
        >
          <div
            className="
              relative flex items-center
              shrink-0
              bg-[rgb(var(--card-2-rgb))]
              border-r border-[rgb(var(--border-rgb))]
              max-w-[50%]
              rounded-l-xl
            "
          >
            <span className="sr-only" id="navbar-cat-label">
              Categoría
            </span>
            <Dropdown
              align="left"
              trigger={({ open, toggle }) => (
                <button
                  type="button"
                  onClick={toggle}
                  aria-labelledby="navbar-cat-label"
                  aria-expanded={open}
                  className="
                    inline-flex items-center justify-between
                    w-auto max-w-full
                    h-10 md:h-11 lg:h-12 xl:h-[3.05rem]
                    px-3
                    text-[11px] md:text-xs lg:text-sm xl:text-[15px]
                    bg-transparent
                  "
                  disabled={catsLoading || catsError}
                >
                  <span className="truncate whitespace-nowrap text-left">
                    {currentCatLabel}
                  </span>
                  <ChevronDown
                    size={16}
                    className={`ml-1 shrink-0 opacity-70 transition-transform duration-150 ${
                      open ? "rotate-180" : ""
                    }`}
                  />
                </button>
              )}
              items={[
                {
                  label: "Todas las categorías",
                  onSelect: () => setCat("all"),
                },
                ...parentCats.flatMap((p) => [
                  {
                    label: `${p.name} (todo)`,
                    onSelect: () => setCat(p.slug),
                  },
                  ...(p.sub || []).map((s) => ({
                    label: `· ${s.name}`,
                    onSelect: () => setCat(s.slug),
                  })),
                ]),
              ]}
            />
          </div>

          <div className="relative flex-1 min-w-0">
            <Input
              ref={inputRef}
              id="navbar-q"
              name="q"
              autoComplete="off"
              placeholder="Buscar productos…"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              onFocus={() => {
                if ((suggestions?.items?.length ?? 0) > 0) {
                  setShowSuggestions(true);
                }
              }}
              className="
                h-10 md:h-11 lg:h-12 xl:h-[3.05rem]
                border-0
                rounded-none
                bg-transparent
                pl-3 pr-11
                text-[12px] md:text-sm lg:text-[15px]
                focus:ring-0 focus:outline-none
              "
            />
            <button
              type="submit"
              aria-label="Buscar"
              onClick={handleSearchClick}
              className="
                absolute right-1.5 top-1/2 -translate-y-1/2
                inline-flex h-8 w-8 md:h-9 md:w-9 items-center justify-center
                rounded-lg
                bg-[rgb(var(--primary-rgb))]
                text-[var(--kw-fg-on-primary)]
                hover:bg-[rgb(var(--primary-rgb)/0.9)]
                transition-colors
              "
            >
              <Search size={16} />
            </button>
          </div>
        </div>

        {/* Sprint 13 polish — dropdown SIEMPRE renderizado (no se quita del
            DOM con &&), con transición opacity + translate-y para un
            fade-in/out suave. pointer-events controla los clicks cuando está
            oculto para no robar foco. */}
        <div
          aria-hidden={!showSuggestions}
          className={[
            "absolute left-0 right-0 mt-2 z-dropdown",
            "rounded-2xl border border-[rgb(var(--border-rgb))]",
            "bg-[rgb(var(--card-rgb))]",
            "shadow-xl overflow-hidden",
            "transition-all duration-200 ease-out",
            showSuggestions && term.trim().length >= MIN_QUERY_LENGTH
              ? "opacity-100 translate-y-0 pointer-events-auto"
              : "opacity-0 -translate-y-1 pointer-events-none",
          ].join(" ")}
        >
          <div className="max-h-[420px] overflow-auto">
            {suggestionsLoading ? (
              // Skeleton: 3 filas con thumb + 2 líneas de texto. Mantiene
              // altura estable mientras llega la respuesta (no salto brusco).
              <div className="px-3 py-3 space-y-3">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 animate-pulse"
                  >
                    <div className="h-9 w-9 rounded-md bg-[rgb(var(--muted-rgb))]" />
                    <div className="flex-1 space-y-1.5">
                      <div className="h-3 w-3/4 rounded bg-[rgb(var(--muted-rgb))]" />
                      <div className="h-2.5 w-1/3 rounded bg-[rgb(var(--muted-rgb))]" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (suggestions?.items?.length ?? 0) === 0 ? (
              <div className="px-4 py-4 text-sm opacity-70">
                No se encontraron sugerencias.
              </div>
            ) : (
              <>
                {categorySuggestions.length > 0 && (
                  <div className="border-b border-[rgb(var(--border-rgb))]">
                    <div className="px-4 pt-3 pb-2 text-[11px] font-semibold uppercase tracking-wide opacity-60">
                      Categorías
                    </div>
                    <div className="pb-2">
                      {categorySuggestions.map((item) => (
                        <button
                          key={`cat-${item.id}`}
                          type="button"
                          onClick={() => handleSuggestionClick(item)}
                          className="
                            w-full text-left px-4 py-2
                            hover:bg-[rgb(var(--card-2-rgb))]
                            transition-colors text-sm
                          "
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {productSuggestions.length > 0 && (
                  <div>
                    <div className="px-4 pt-3 pb-2 text-[11px] font-semibold uppercase tracking-wide opacity-60">
                      Productos
                    </div>
                    <div className="pb-2">
                      {productSuggestions.map((item) => (
                        <button
                          key={`prod-${item.id}`}
                          type="button"
                          onClick={() => handleSuggestionClick(item)}
                          className="
                            w-full text-left px-4 py-2
                            hover:bg-[rgb(var(--card-2-rgb))]
                            transition-colors text-sm
                          "
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="border-t border-[rgb(var(--border-rgb))] px-3 py-2">
                  <button
                    type="button"
                    onClick={() => handleSearchTerm(term, cat)}
                    className="
                      w-full rounded-xl px-3 py-2 text-sm font-medium
                      bg-[rgb(var(--card-2-rgb))]
                      hover:bg-[rgb(var(--muted-rgb))]
                      transition-colors
                    "
                  >
                    Ver todos los resultados
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </form>
  );
}
